import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { buildApiUrl } from '../constants/api.constants';

/**
 * Frontend JavaScript hibák backendre küldéséért felelős service.
 *
 * Szándékosan fetch()-et használ HttpClient helyett, hogy:
 *  - bypass-olja az Angular interceptorokat (nincs végtelen loop ha HTTP hiba esetén hívjuk)
 *  - az app teardown fázisában is működjön
 *
 * Rate-limiting: max 20 bejegyzés/perc, 5 másodperces deduplikáció.
 */
@Injectable({ providedIn: 'root' })
export class FrontendLoggerService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly endpoint = buildApiUrl('/api/system/logs/frontend');

  // Rate limiting
  private sentCount = 0;
  private countResetAt = Date.now();
  private readonly MAX_PER_MINUTE = 20;
  private readonly DEDUP_WINDOW_MS = 5000;
  private readonly recentMessages = new Map<string, number>();

  /**
   * Log bejegyzés küldése a backendre. Fire-and-forget, hibát csendben elnyel.
   */
  sendLog(
    level: 'WARNING' | 'ERROR' | 'CRITICAL',
    message: string,
    stackTrace?: string | null,
  ): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.shouldSend(message)) return;

    const body = JSON.stringify({
      level,
      message: message.substring(0, 2000),
      stack_trace: stackTrace ? stackTrace.substring(0, 10000) : null,
      browser_url: window.location.href,
      user_agent: navigator.userAgent,
    });

    // Token opcionális – bejelentkezési hibánál nincs token, de a logot mégis el kell küldeni
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(this.endpoint, {
      method: 'POST',
      headers,
      body,
    }).catch(() => {
      /* silent – nem kell hibát logolni, hogy ne okozzunk végtelen ciklust */
    });
  }

  private shouldSend(message: string): boolean {
    const now = Date.now();

    // Percenként visszaállítjuk a számlálót
    if (now - this.countResetAt > 60_000) {
      this.sentCount = 0;
      this.countResetAt = now;
      this.recentMessages.clear();
    }

    if (this.sentCount >= this.MAX_PER_MINUTE) return false;

    // Duplikált üzenet szűrés
    const lastSent = this.recentMessages.get(message);
    if (lastSent !== undefined && now - lastSent < this.DEDUP_WINDOW_MS) return false;

    this.recentMessages.set(message, now);
    this.sentCount++;
    return true;
  }
}
